import { queryClient } from '@/lib/query';
import { QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/styles/index.css';

import { TooltipProvider } from '@/components/ui/tooltip';
import { bootstrapMockAuth } from '@/lib/auth/mockAuth';
import { config } from '@/lib/config';
import { initFaro } from '@/lib/faro';
import { MqttConnector } from '@/lib/mqtt/connector';
import { initMqttSource } from '@/lib/mqtt/source';
import { subscribeOiAutoDeselect } from '@/lib/oi/autoDeselect';
import { ErrorBoundary, initSentry } from '@/lib/sentry';
import { StalenessWatchdog } from '@/lib/staleness/watchdog';
import { App } from './App';

async function bootstrap() {
  // Init error tracking first so early bootstrap failures are still captured.
  // Sentry + Faro run side by side while we confirm logs reach Grafana.
  initSentry();
  initFaro();

  // Start MSW for mock REST when in mock data source
  if (config.isMock) {
    const { worker } = await import('@/lib/api/mock/browser');
    await worker.start({ onUnhandledRequest: 'bypass', quiet: true });
    bootstrapMockAuth();
  }

  // Real Keycloak login (86e232ykg) — orthogonal to the MQTT/REST mock vs
  // live toggle above. A tenant can run mock drone data with real auth, or
  // vice versa, so this isn't gated on config.isMock. Runs even with
  // config.features.keycloakAuth off: it also clears any persisted session
  // that has no live Keycloak session behind it (ENG-278).
  const { initKeycloakAuth } = await import('@/lib/auth/keycloak');
  await initKeycloakAuth();

  // Build the MQTT singleton before the first render so `getMqttSource()`
  // stays synchronous for consumers. In mock mode this awaits the lazily
  // imported MqttSourceMock chunk; in live mode it resolves immediately.
  // Constructing a source is side-effect-free — nothing connects or emits
  // until MqttConnector calls `connect()`.
  await initMqttSource();

  // ENG-206 — drop the OI selection once it ends. Lives as long as the app.
  subscribeOiAutoDeselect();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <TooltipProvider delayDuration={200}>
            <MqttConnector />
            <StalenessWatchdog />
            <ErrorBoundary
              fallback={
                <div className="flex h-dvh flex-col items-center justify-center gap-3 bg-(--color-bg) text-(--color-text)">
                  <p className="text-sm opacity-70">Something went wrong.</p>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="rounded-md border border-(--color-border) px-3 py-1.5 text-sm"
                  >
                    Reload
                  </button>
                </div>
              }
            >
              <App />
            </ErrorBoundary>
            <Toaster position="top-right" richColors closeButton />
          </TooltipProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

void bootstrap();
